import { createSelector } from 'reselect';
import moment from 'utils/cops-moment';
import sortBy from 'lodash.sortby';

const round = n => num => num && +num.toFixed(n);
export const roundTwo = round(2);
export const getEquipmentsTableData = createSelector(
  [state => state.dashboard.data.equipments || []],
  equipments =>
    equipments &&
    equipments?.map(eq => ({
      ...eq,
      ageFormatted: `${eq.simpleAge} yr`,
      isCompetitor: eq.isCompetitor ? 'YES' : 'NO',
      isLeader: eq.isLeader ? 'YES' : '',
      actualRunningHoursPerYear: roundTwo(eq.actualRunningHoursPerYear),
      estimatedRunningHours: roundTwo(eq.estimatedRunningHours),
      actualRunningHours: roundTwo(eq.actualRunningHours),
      key: eq.id, // key is required when using Table component
    })),
);

export const getKPIs = createSelector(
  [state => state.dashboard.data.kpis || {}, state => state.navigation.navigationSelf.id || ''],
  (kpis, currentId) => {
    if (Object.keys(kpis).length > 0) {
      const allKpis = [];
      if (kpis.openTickets) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.OPEN_TICKETS',
          value: kpis.openTickets.value,
          clickThrough: true,
          uri: `tickets/object/${currentId}`,
        });
      }

      if (kpis.wipOrdersCount) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.WIP_ORDERS',
          value: kpis.wipOrdersCount.value,
          clickThrough: true,
          uri: 'timeline/status/wip',
        });
      }
      if (kpis.wipOrdersValue) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.WIP',
          value: kpis.wipOrdersValue.value,
          currencyCode: kpis.wipOrdersValue.uom,
          currencyTranslationKey:
            kpis.wipOrdersValue.uom &&
            `dashboard.kpis.uom.${kpis.wipOrdersValue.uom}`,
          clickThrough: true,
          uri: 'timeline/status/wip',
        });
      }
      if (kpis.pcnfOrdersCount) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.PCNF_ORDERS',
          value: kpis.pcnfOrdersCount.value,
          clickThrough: false,
        });
      }
      if (kpis.backlogOrdersCount) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.BACKLOG_ITEMS',
          value: kpis.backlogOrdersCount.value,
          clickThrough: true,
          uri: 'timeline/status/backlog',
        });
      }
      if (kpis.backlogHoursValue) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.BACKLOG',
          value: kpis.backlogHoursValue.value,
          unitTranslationKey:
            kpis.backlogHoursValue.uom &&
            `dashboard.kpis.uom.${kpis.backlogHoursValue.uom}`,
          clickThrough: true,
          uri: 'timeline/status/backlog',
        });
      }
      if (kpis.contractLinesCount) {
        allKpis.push({
          labelTranslationKey: 'dashboard.kpis.CONTRACT_LINES',
          value: kpis.contractLinesCount.value,
          clickThrough: false,
        });
      }
      return allKpis;
    }
    return [];
  },
);

export const getShortTermTimeline = createSelector(
  [state => state.dashboard.data.shortTermTimeLineSegments || {}],
  segments =>
    Object.keys(segments).reduce(
      (acc, curr) =>
        segments[curr]
          ? {
            ...acc,
            [curr]: {
              ...segments[curr],
              relativeNotation: moment(segments[curr].segmentDate).from(
                moment().set({ minute: 0, hour: 0, second: 0 }),
              ),
            },
          }
          : acc,
      {},
    ),
);

export const getInstalledAtsTableData = createSelector(
  [
    state =>
      (Array.isArray(state.dashboard.data.installedAts) &&
        state.dashboard.data.installedAts) ||
      [],
  ],
  (installedAts) => {
    const tableData = installedAts?.map(installedAt => ({
      ...installedAt,
      key: installedAt.number,
    }));
    return sortBy(tableData, 'number');
  },
);

export const getServicesTableData = createSelector(
  [
    state =>
      (Array.isArray(state.dashboard.data.services) &&
        state.dashboard.data.services) ||
      [],
  ],
  services =>
    services?.map((service) => {
      let key = service.id;
      if (!key) {
        key = service.description;
      }
      return { ...service, key };
    }),
);

export const getContactsTableData = createSelector(
  [
    state =>
      (
        (state.dashboard.data.equipment && state.dashboard.data.contracts) ||
        (state.dashboard.data.functionalLocation && state.dashboard.data.contracts)
      ) || [],
  ],
  contracts =>
    contracts?.map(contract => ({
      ...contract,
      isActive: contract.isActive ? 'YES' : 'NO',
      key: contract.id,
    })),
);
